define("Modules/SwipableImageGroup", [`Module`, `module`], function (Super, Self) {
	const AVAILABLE_BACKGROUNDS = [`cover`, `contain`];
	return Super.inherit({
		namespace: Self.id,
		requires: [`XHR`],
		defaults: {
			backgroundSize: `cover`, // cover or contain
			imageUrls: [], // accepts array of strings
			onImageSelectionChange: null, // accepts function(indexOfSelectedImage)
			displayArrowsOnDesktop: true,
			displayArrowsOnMobile: true,
			isSwipable: true,
			lazyLoadImages: false,
			onClick: null, // accepts function
		},
		constructor(setup) {
			const { data, App } = this;
			const {
				imageUrls,
				onImageSelectionChange,
				displayArrowsOnDesktop,
				displayArrowsOnMobile,
				isSwipable,
				lazyLoadImages,
				onClick,
			} = setup;
			const backgroundSize = this.includes(AVAILABLE_BACKGROUNDS, setup.backgroundSize)
				? setup.backgroundSize
				: `cover`;

			this.assign(data, {
				backgroundSize,
				imageUrls,
				onImageSelectionChange,
				displayArrowsOnDesktop,
				displayArrowsOnMobile,
				isSwipable,
				isUnseen: true,
				currentImageIndex: 0,
				loaded: false,
				onClick,
				isModuleReadyForAnimation: true, // Prevents users from swiping or clicking arrows too fast
			});

			if (displayArrowsOnDesktop) {
				this.addClass(`display-arrows-on-desktop`);
			}

			if (displayArrowsOnMobile) {
				this.addClass(`display-arrows-on-mobile`);
			}

			this.render();

			if (lazyLoadImages) {
				App.onScroll(this.loadImages);
				App.onTick(this.loadImages);
			} else {
				this.loadImages();
			}

			if (this.isFunction(onClick)) {
				this.addClass(`clickable`);
			}

			this.addClass(`background-size-${backgroundSize}`);
		},

		api: {
			loadImages: {
				value() {
					const { DOM, data, dimensions } = this;
					const { imageUrls, currentImageIndex } = data;
					let { isUnseen } = data;
					const imageListElement = this.get(`.image-list`);

					if (isUnseen) {
						if (dimensions.inViewport) {
							data.isUnseen = false;
						} else {
							return;
						}
					} else {
						return;
					}

					this.each(imageUrls, (imageUrl, index) => {
						if (DOM.inViewport(imageListElement)) {
							if (this.isString(imageUrl, 1)) {
								if (index === currentImageIndex) {
									this.setImageSource(this.get(`.image-container.center`), imageUrl, true);
								}
							}
						}
					});
				},
			},

			setImageSource: {
				value(imageContainerElement, imageUrl, isFirstImageLoad = false) {
					const { XHR, container, data, dimensions } = this;
					const { backgroundSize } = data;
					const imageContainerAspectRatio = dimensions.width / dimensions.height;
					const imageElement = this.get(`.image`, imageContainerElement);

					return XHR.loadImage({
						src: imageUrl,
						then: (newImage) => {
							this.deferUICalculations(container, () => {
								const newImageAspectRatio = newImage.naturalWidth / newImage.naturalHeight;

								if (backgroundSize === `cover`) {
									imageElement.style.backgroundImage = `url('${imageUrl}')`;
								} else {
									// contain
									imageElement.setAttribute(`src`, imageUrl);
									if (newImageAspectRatio < imageContainerAspectRatio) {
										imageElement.style.width = `100%`;
										imageElement.style.objectFit = `cover`;
									} else {
										imageElement.style.width = `auto`;
									}

									if (newImage.naturalWidth !== dimensions.width) {
										const leftScroll = (dimensions.height * newImageAspectRatio) / 2 - dimensions.width / 2;
										imageContainerElement.scrollTo({
											left: leftScroll,
											top: 0,
										});
									}
								}
								this.addClass(`loaded`);
								data.loaded = true;

								this.updateScrollImageButtons();
							});
						},
						fail: (reason) => {
							// swallow loading error
						},
						done: true,
					});
				},
			},

			deferUICalculations: {
				// TODO: make util function
				value(target, callback) {
					const { DOM } = this;
					const epsilon = 1;

					if (DOM.getDimensions(target).width <= epsilon) {
						this.defer({
							duration: 100,
							then: () => {
								this.deferUICalculations(target, callback);
							},
						});
					} else {
						callback();
					}
				},
			},

			onNextImage: {
				public: true,
				value() {
					const { data } = this;
					const { currentImageIndex, imageUrls } = data;

					if (currentImageIndex < imageUrls.length - 1) {
						this.selectImageByIndex(currentImageIndex + 1);
					}
				},
			},

			onPreviousImage: {
				public: true,
				value() {
					const { data } = this;
					const { currentImageIndex } = data;

					if (currentImageIndex > 0) {
						this.selectImageByIndex(currentImageIndex - 1);
					}
				},
			},

			selectImageByIndex: {
				public: true,
				value(context, newImageIndex) {
					const { data } = this;
					const { imageUrls, isModuleReadyForAnimation, onImageSelectionChange, previousImageIndex } = data;
					const adjustedPreviousImageIndex = this.isInt(previousImageIndex) ? previousImageIndex : 0;

					if (
						isModuleReadyForAnimation &&
						newImageIndex >= 0 &&
						newImageIndex != adjustedPreviousImageIndex &&
						this.isArray(imageUrls, newImageIndex + 1)
					) {
						const previousFocusImageContainer = this.get(`.image-container.center`);
						let focusImageContainer = this.get(`.image-container.center`);

						data.isModuleReadyForAnimation = false;

						if (adjustedPreviousImageIndex < newImageIndex) {
							const inactiveElement = this.get(`.image-container.left`);

							focusImageContainer = this.get(`.image-container.right`);

							this.removeClass(`left`, inactiveElement);
							this.addClass(`inactive right`, inactiveElement);
							this.addClass(`left`, previousFocusImageContainer);
						} else {
							const inactiveElement = this.get(`.image-container.right`);

							focusImageContainer = this.get(`.image-container.left`);

							this.removeClass(`right`, inactiveElement);
							this.addClass(`inactive left`, inactiveElement);
							this.addClass(`right`, previousFocusImageContainer);
						}

						this.removeClass(`center in-focus`, previousFocusImageContainer);
						this.removeClass(`right left inactive`, focusImageContainer);
						this.addClass(`in-focus center`, focusImageContainer);

						if (this.isFunction(onImageSelectionChange)) {
							onImageSelectionChange(newImageIndex);
						}

						this.setImageSource(focusImageContainer, imageUrls[newImageIndex]);

						data.currentImageIndex = newImageIndex;
						data.previousImageIndex = newImageIndex;

						this.defer({
							duration: 400, // length of swipe animation
							then: () => {
								data.isModuleReadyForAnimation = true;
							},
						});
					}
				},
			},

			updateScrollImageButtons: {
				value() {
					const { data } = this;
					const { imageUrls, currentImageIndex, loaded } = data;
					const nextButtonElement = this.get(`.next-image-button`);
					const previousButtonElement = this.get(`.previous-image-button`);

					if (nextButtonElement != null && previousButtonElement != null) {
						if (imageUrls.length === 1) {
							this.hide({ target: nextButtonElement });
							this.hide({ target: previousButtonElement });
							return;
						} else {
							this.show({ target: nextButtonElement });
							this.show({ target: previousButtonElement });
						}
					}

					if (nextButtonElement != null) {
						if (currentImageIndex < imageUrls.length - 1 && loaded) {
							this.removeClass(`inactive`, nextButtonElement);
						} else {
							this.addClass(`inactive`, nextButtonElement);
						}
					}

					if (previousButtonElement != null) {
						if (currentImageIndex > 0 && loaded) {
							this.removeClass(`inactive`, previousButtonElement);
						} else {
							this.addClass(`inactive`, previousButtonElement);
						}
					}
				},
			},
		},

		listeners: [
			{
				event: `click`,
				handler(evt) {
					const { data } = this;
					const action = evt.target.getAttribute(`action`);

					switch (action) {
						case `onNextImage`:
							this.onNextImage();
							break;
						case `onPreviousImage`:
							this.onPreviousImage();
							break;
						default:
							if (this.isFunction(data.onClick)) {
								data.onClick();
							}
					}
				},
			},

			{
				event: `touchstart`,
				handler(evt) {
					const { data } = this;
					const { isSwipable } = data;

					if (isSwipable) {
						data.startTouchPosition = evt.touches[0].clientX;
						data.lastTouchPosition = data.startTouchPosition;
					}
				},
			},

			{
				event: `touchmove`,
				handler(evt) {
					const { data } = this;
					const { isSwipable } = data;

					if (isSwipable) {
						data.lastTouchPosition = evt.touches[0].clientX;
					}
				},
			},

			{
				event: `touchend`,
				handler(evt) {
					const { data } = this;
					const { isSwipable } = data;
					const touchMovementThreshold = 50;

					if (isSwipable) {
						if (data.startTouchPosition - data.lastTouchPosition > touchMovementThreshold) {
							this.onNextImage();
						}
						if (data.startTouchPosition - data.lastTouchPosition < -touchMovementThreshold) {
							this.onPreviousImage();
						}
					}
				},
			},
		],
	});
});

define("Modules/SwipableImageGroup.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Modules/SwipableImageGroup/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "scrollable";
},"3":function(container,depth0,helpers,partials,data) {
    return " <div class=\"image\"></div>  ";
},"5":function(container,depth0,helpers,partials,data) {
    return " <img class=\"image\"> ";
},"7":function(container,depth0,helpers,partials,data) {
    return " <div class=\"image\"></div> ";
},"9":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a href=\"#\" action=\"onPreviousImage\" class=\"previous-image-button\" aria-label=\"view previous image\" style=\"display:none;\">"
    + alias2(lookupProperty(helpers,"getIcon").call(alias1,"arrow-left",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":891},"end":{"line":1,"column":915}}}))
    + "</a> <a href=\"#\" action=\"onNextImage\" class=\"next-image-button\" aria-label=\"view next image\" style=\"display:none;\">"
    + alias2(lookupProperty(helpers,"getIcon").call(alias1,"arrow-right",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1030},"end":{"line":1,"column":1055}}}))
    + "</a> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"image-list\"> <div class=\"image-container "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"backgroundSize") : depth0),"==","contain",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":53},"end":{"line":1,"column":117}}})) != null ? stack1 : "")
    + " left\"> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"backgroundSize") : depth0),"cover",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":1,"column":125},"end":{"line":1,"column":284}}})) != null ? stack1 : "")
    + " </div> <div class=\"image-container "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"backgroundSize") : depth0),"==","contain",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":320},"end":{"line":1,"column":384}}})) != null ? stack1 : "")
    + " center in-focus\"> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"backgroundSize") : depth0),"cover",{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":1,"column":403},"end":{"line":1,"column":506}}})) != null ? stack1 : "")
    + " </div> <div class=\"image-container "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"backgroundSize") : depth0),"==","contain",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":542},"end":{"line":1,"column":606}}})) != null ? stack1 : "")
    + " right\"> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"backgroundSize") : depth0),"cover",{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":1,"column":615},"end":{"line":1,"column":718}}})) != null ? stack1 : "")
    + " </div> </div> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"imageUrls") : depth0)) != null ? lookupProperty(stack1,"length") : stack1),1,">",{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":733},"end":{"line":1,"column":1072}}})) != null ? stack1 : "")
    + " "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(alias1,"tixr",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1073},"end":{"line":1,"column":1091}}}));
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=SwipableImageGroup.js.map